import type { PlanningLastUpdate } from '@/modules/worker/types/workerList';
import type { IAddress } from '@/types/address';
import type { SortOrder, Timestamp } from '@/types/common';
import type { EContractType } from '@/types/contract';
import type { JobExperience } from '@/types/job';
import type { MissionStatus, WorkerMissionStatus } from '@/types/mission';
import type {
	CV,
	CVType,
	IRegularAvailabilities,
	IWorkerHabilitation,
} from '@/types/worker';

// --- Enums ---

export enum MobileAppStatus {
	NEW = 'NEW',
	INTERESTING = 'INTERESTING',
	TO_FOLLOW = 'TO_FOLLOW',
	MEETING = 'MEETING',
	MET = 'MET',
	INTEGRATED = 'INTEGRATED',
	INVITED = 'INVITED',
	REINVITED = 'REINVITED',
	IMPORTED = 'IMPORTED',
	REGISTERED = 'REGISTERED',
	REJECTED = 'REJECTED',
	NO_LONGER_REGISTERED = 'NO_LONGER_REGISTERED',
}

export enum WorkerType {
	INTERIM = 'INTERIM',
	CANDIDATE = 'CANDIDATE',
}

export interface WorkerProfileBesoin {
	jobId: string;
	companyId: string;
	companyName: string;
	besoinWorkerStatus: string;
	agencyId: string;
	agencyName: string;
}

export enum WorkerCandidateStatus {
	ARCHIVED = 'ARCHIVED',
	REJECTED = 'REJECTED',
	NEW = 'NEW',
	INTERESTING = 'INTERESTING',
	MEETING = 'MEETING',
	MET = 'MET',
	INTEGRATED = 'INTEGRATED',
	TO_FOLLOW = 'TO_FOLLOW',
}

export enum FetchWorkersSort {
	NAME = 'name',
	CREATED_AT = 'createdAt',
	UPDATED_AT = 'updatedAt',
	LAST_MISSION = 'lastMission',
	CITY = 'city',
}

export enum FetchWorkersMode {
	LIST = 'list',
	PLANNING = 'planning',
	NOT_PAGINATE = 'not_paginate',
}

export enum WorkerMissionsFiltersKeys {
	STATUS = 'status',
	WORKER_STATUS = 'workerStatus',
	JOB = 'job',
	CONTRACT_TYPE = 'contractType',
}

export enum WorkPreferencesTeamPreference {
	ALONE = 'alone',
	TEAM = 'team',
	BOTH = 'both',
}

export enum RegistrationCreatedFrom {
	APP_MOBILE = 'APP_MOBILE',
	AGENCY = 'AGENCY',
	BDU = 'BDU',
}

export enum RegistrationIdentityCheckState {
	TO_VERIFY = 'TO_VERIFY',
	PENDING = 'PENDING',
	INVALID = 'INVALID',
}

export enum UpdateRegistrationStatus {
	IN_PROGRESS = 'IN_PROGRESS',
}

export enum EGender {
	MALE = 'M',
	FEMALE = 'F',
}

export enum SyncWorkerToEvoliaResultStatus {
	CREATED = 'created',
	UPDATED = 'updated',
	SKIPPED = 'skipped',
	FAILED = 'failed',
}

export enum SyncWorkerToEvoliaResultReason {
	NO_AGENCY_CODE_RESOLVABLE = 'no_agency_code_resolvable',
	EVOLIA_DISABLED = 'evolia_disabled',
	MISSING_BIRTHDATE = 'missing_birthdate',
}

// --- Worker domain models ---

export interface WorkerListItem {
	id: string;
	createdAt: Date | null;
	updatedAt: Date | null;
	firstname: string | null;
	lastname: string | null;
	email: string | null;
	phone: string | null;
	picture: string | null;
	delegable: boolean;
	addressCity: string | null;
	longitude: number | null;
	latitude: number | null;
	vehicle: boolean;
	nightShift: boolean;
	weekendShift: boolean;
	workerType: WorkerType | null;
	mobileAppStatus: MobileAppStatus | null;
	candidateStatus: WorkerCandidateStatus | null;
	jobIds: string[];
	cvPath: string | null;
	anonymizedCvPath: string | null;
	agencyId: string | null;
	evoliaMatricule: string | null;
	lastContactAt: Date | null;
	habilitations: IWorkerHabilitation[];
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	askedChanges: any;
	hasPendingAskedChanges: boolean;
	hasPendingPaymentRequest: boolean;
	hasLeftRegistrationsTracking: boolean;
}

type ContractPreferenceType = Exclude<
	EContractType,
	EContractType.NONE | EContractType.SEASONAL
>;

export type WorkerContractPreferences = {
	[K in ContractPreferenceType as Lowercase<K>]: boolean;
};

export interface WorkerJob {
	jobId: string;
	jobName: string | null;
	experience: JobExperience | null;
	degreeLevel: string | null;
}

export interface WorkerDetail {
	id: string;
	evoliaId: number | null;
	delegable: boolean;
	gender?: EGender;
	firstname: string;
	lastname: string;
	email?: string;
	phone?: string;
	cv: CV | null;
	picture?: string | null;
	workerType: WorkerType | null;
	mobileAppStatus: MobileAppStatus | null;
	candidateStatus: WorkerCandidateStatus | null;
	address?: IAddress;
	evoliaAddress?: IAddress & {
		streetNumber?: string;
		streetNumberExtra?: string;
		bis?: string;
		country?: string;
	};
	originOfApplication?: string;

	companies: {
		refused: string[];
		approved: string[];
	};

	jobs: WorkerJob[];

	agencies: {
		id?: string;
		agencyId: string;
		publicName: string | null;
		agencyName?: string | null;
		mobileAppStatus: string | null;
		candidateStatus: string | null;
	}[];

	regularAvailabilities: IRegularAvailabilities[];
	nightShift: boolean;
	weekendShift: boolean;
	maxTravelDistance?: number;
	maxTravelTime?: number;
	birthDate?: number;
	birthPlace?: string;
	birthCountry?: string;
	birthName?: string;
	nationality?: string;
	nationalityCode?: string;
	nirKey?: string;
	nir?: string;
	referenceCheck?: string;
	city?: string;
	salaryExpected?: number;
	teamPreference?: string;
	vehicle: boolean;
	carpooling: boolean;
	registrationDate?: number;
	planningLastUpdate?: PlanningLastUpdate | null;
	blacklist?: { companyId: string; companyName: string; from: string }[];
	prospectCompanies?: { companyId: string; companyName: string }[];
	contractPreference?: WorkerContractPreferences;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	stats?: any;
	createdAt: number;
	updatedAt: number;
	origin: string;
	keywords?: string[];
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	askedChanges: any;
	anonymizedCv: CV | null;
	generatedCv?: CV | null;
	nonDelegableReason?: string;
	deactivationReasonId?: string | null;
	besoins?: WorkerProfileBesoin[];
	lastContactAt?: Date | null;
	hasLeftRegistrationsTracking?: boolean;
	lastApplicationIdentity?: WorkerApplicationIdentity | null;
}

export interface WorkerApplicationIdentity {
	firstname: string | null;
	lastname: string | null;
	email: string | null;
	phone: string | null;
	source: string | null;
	jobOfferId: string | null;
	appliedAt: string;
}

export interface LoadedWorker {
	id: string;
	firstname: string;
	lastname: string;
	jobNames: { id: string; name: string }[];
	manager: string;
	phone: string;
	email: string;
	city: string;
	vehicle: boolean;
	weekendShift: boolean;
	nightShift: boolean;
	status: string;
	geolocated: boolean;
	inactiveByAgencies: string;
	askedChanges: string;
	agenciesList: string[];
	createdAt: number;
	picture: string;
	collection?: string;
	cv?: CV;
	anonymizedCv?: CV;
}

// --- Missions ---

export interface InterimMission {
	id: string;
	name: string;
	companyId: string;
	status: MissionStatus;
	missionId: string;
	contractType: string;
	start: Timestamp;
	workerStatus: WorkerMissionStatus;
	company: {
		id: string;
		name: string;
	};
	jobId: string;
	lastActionTimestamp: Timestamp;
	isLegacy?: boolean;
}

export interface WorkerMissionMetadatasResponse {
	total: number;
	missionStatus: Partial<Record<MissionStatus, number>>;
	workerStatus: Partial<Record<WorkerMissionStatus, number>>;
	contractType: Partial<Record<EContractType, number>>;
	job: WorkerMissionMetadataJobEntry[];
}

export interface WorkerMissionMetadataJobEntry {
	jobId: string;
	count: number;
}

export interface WorkerMissionMetadatas {
	missionStatus: WorkerMissionMetadatasItem[];
	interimStatus: WorkerMissionMetadatasItem[];
	jobs: WorkerMissionMetadatasItem[];
	contractType: WorkerMissionMetadatasItem[];
}

export interface WorkerMissionMetadatasItem {
	key: string;
	count: number;
}

export interface WorkerMissionsPaginationParams {
	page?: number;
	limit?: number;
	sortField?: string;
	sortOrder?: number;
}

export interface WorkerMissionsFilters {
	companyId?: string;
	search?: string;
	status?: string;
	workerStatus?: WorkerMissionStatus[];
	jobId?: string;
	contractType?: string;
}

export type WorkerMissionsListQueryParams = WorkerMissionsPaginationParams &
	WorkerMissionsFilters;

export interface WorkerMissionsFiltersApiParams
	extends Omit<WorkerMissionsFilters, 'workerStatus'> {
	workerStatus?: string;
}

export interface WorkerMissionsListApiParams
	extends WorkerMissionsFiltersApiParams {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}

// --- CV & Resume ---

export interface CvData {
	createdAt: string;
	expiresAt: string;
	id: string;
	path: string;
	signedUrl: string;
	type: CVType;
	updatedAt: string;
	userId: string;
	workerId: string;
}

export interface ParsedData {
	firstname: string | null;
	lastname: string | null;
	email: string | null;
	phone: string | null;
	address: {
		city: string | null;
		zip: string | null;
		street: string | null;
	} | null;
	birthday_timestamp: number | null;
}

// --- Profile preview ---

export interface ProfilePreviewJob {
	jobId: string;
	experience: JobExperience | null;
}

export interface ProfilePreviewCV {
	path: string | null;
	expiresAt?: number;
}

export interface ProfilePreviewAgency {
	id: string;
	publicName: string;
}

export interface ProfilePreviewResponse {
	id: string;
	firstname: string;
	lastname: string;
	email: string | null;
	phone: string | null;
	birthDate: string | null;
	address: IAddress | null;
	nationalityCode: string | null;
	birthPlace: string | null;
	birthCountry: string | null;
	nir: string | null;
	jobs: ProfilePreviewJob[];
	cv: ProfilePreviewCV | null;
	recruitmentCount: number;
	roles: string[];
	agencies?: ProfilePreviewAgency[];
}

// --- Registrations ---

export interface RegistrationWorker {
	workerId: string;
	firstname: string;
	lastname: string;
	phone: string;
	createdAt: Timestamp;
	mobileAppStatus?: MobileAppStatus;
}

export interface RegistrationWorkerToTreat extends RegistrationWorker {
	createdFrom: RegistrationCreatedFrom;
}

export interface RegistrationWorkerIdentityCheck extends RegistrationWorker {
	state: RegistrationIdentityCheckState;
	documentId: string | null;
	rejectedAt?: string | null;
}

export interface RegistrationsByStatus {
	toTreat: RegistrationWorkerToTreat[];
	inProgress: RegistrationWorker[];
	toValidate: RegistrationWorker[];
	identityCheck: RegistrationWorkerIdentityCheck[];
}

export interface RegistrationCompletionAdministrativeData {
	firstname: boolean;
	lastname: boolean;
	civility: boolean;
	phone: boolean;
	validEmail: boolean;
	birthdate: boolean;
	birthplace: boolean;
	birthcountry: boolean;
	nationality: boolean;
	administrativeAddress: boolean;
	localizedAddress: boolean;
	nir: boolean;
	socialSecurityDocument: boolean;
	iban: boolean;
	interview: boolean;
	completion: number;
	validationStatus?: string | null;
	validatedAt?: string | null;
	validatedByAgencyId?: string | null;
	validatedByCollaboratorId?: string | null;
}

export interface RegistrationCompletionIdentity {
	document: boolean;
	sendToCheck: boolean;
	documentId?: string;
	verified?: boolean;
	verifiedAt?: string | null;
}

export interface RegistrationCompletionDetails {
	administrativeData: RegistrationCompletionAdministrativeData;
	identity: RegistrationCompletionIdentity;
}

export interface RegistrationsCount {
	count: number;
}

// --- Query & filter types ---

export interface WorkersMetadata {
	cities: string[];
	keywords: string[];
}

export interface FetchWorkersQuery {
	page?: number;
	limit?: number;
	search?: string;
	ids?: string[];
	status?: MobileAppStatus;
	statusProfil?: string[];
	mobileAppStatuses?: string[];
	available?: boolean;
	lastUpdate?: string;
	lastUpdateBefore?: string;
	jobs?: string[];
	excludedJobIds?: string[];
	documents?: string[];
	agencyId?: string;
	hasVehicle?: boolean;
	hasNightShift?: boolean;
	hasWeekendShift?: boolean;
	hasAskedChanges?: boolean;
	documentExpiring?: boolean;
	delegable?: boolean;
	cities?: string[];
	keywords?: string[];
	approvedCompanyIds?: string[];
	mode?: FetchWorkersMode;
	sort?: FetchWorkersSort;
	order?: SortOrder;
}

export type WorkersMetadataQuery = Omit<
	FetchWorkersQuery,
	'page' | 'limit' | 'mode' | 'sort' | 'order'
>;

// --- API payloads ---

export interface UpdateWorkerRegularAvailabilitiesPayload {
	morning: boolean;
	afternoon: boolean;
	evening: boolean;
	night: boolean;
}

export interface UpdateWorkerSchedulePayload {
	'2x8': boolean;
	'3x8': boolean;
	'4x8': boolean;
	day: boolean;
}

export interface UpdateWorkerSalaryPayload {
	expectedHourlySalary?: number;
}

export interface UpdateWorkerMobilityPayload {
	vehicle: boolean;
	carpooling: boolean;
	maxTravelDistance?: number | null;
	maxTravelTime?: number | null;
}

export interface UpdateWorkPreferencesEnvironmentDto {
	negativeCold?: boolean;
	positiveCold?: boolean;
	noise?: boolean;
	smells?: boolean;
	dust?: boolean;
	sustainedPace?: boolean;
	extendedStanding?: boolean;
	heavyLoads?: boolean;
}

export interface UpdateWorkPreferencesPayload {
	teamPreference?: WorkPreferencesTeamPreference;
	environment?: UpdateWorkPreferencesEnvironmentDto;
}

export type UpdateContractPreferencesPayload = {
	[K in ContractPreferenceType]: boolean;
};

export interface CreateWorkerCVData {
	path: string;
}

export interface CreateWorkerData {
	firstname: string;
	lastname: string;
	email: string;
	phone: string;
	source: string;
	jobs: string[];
	birthName?: string | null;
	birthDate?: string | null;
	cv?: CreateWorkerCVData | null;
	sourceDetail?: string | null;
	raw?: string | null;
	besoinId?: string;
	addToRegistrationsTracking?: boolean;
}

export interface UpdateWorkerPayload {
	firstname?: string;
	lastname?: string;
	birthName?: string;
	gender?: EGender;
	birthday?: Date;
	nationality?: string;
	birthplace?: string;
	birthCountry?: string;
	email?: string;
	phone?: string;
	address?: IAddress;
	nir?: string;
	nirKey?: string;
	vehicle?: boolean;
	carpooling?: boolean;
	maxTravelDistance?: number;
	maxTravelTime?: number;
	nightShift?: boolean;
	weekendShift?: boolean;
	regularAvailabilities?: UpdateWorkerRegularAvailabilitiesPayload[];
	contractPreference?: UpdateContractPreferencesPayload;
	schedule?: UpdateWorkerSchedulePayload;
	teamPreference?: WorkPreferencesTeamPreference;
	environment?: UpdateWorkPreferencesEnvironmentDto;
	expectedHourlySalary?: number;
	referenceCheck?: string;
}

export interface AddJobPayload {
	jobId: string;
	jobCode: string;
	jobName: string;
	experience: JobExperience;
	yearsOfExperience?: number;
	isMain?: boolean;
	degreeLevel?: string | null;
}

export interface UpdateJobExperiencePayload {
	experience: JobExperience;
	experienceYears?: number;
	experienceMonths?: number;
	degreeLevel?: string | null;
}

export interface AddToAgencyPayload {
	agencyId: string;
	agencyName: string;
}

export interface BulkRemoveFromAgencyResult {
	successCount: number;
	failedCount: number;
	failedIds: string[];
}

export interface GenerateCVResponse {
	cvPath: string;
	userId: string;
}

export interface DeleteCVParams {
	type: 'GENERATED' | 'ANONYMIZED';
}

export interface SyncWorkerToEvoliaData {
	createIfMissing: boolean;
}

export interface SyncToEvoliaResult {
	status: SyncWorkerToEvoliaResultStatus;
	evoliaId?: number;
	evoliaMatricule?: string;
	reason?: SyncWorkerToEvoliaResultReason;
}

export interface GetWorkerIdByEmailParams {
	email: string;
}

export interface GetWorkerIdByEmailResponse {
	workerId: string;
}
